/**
 * @file Campaigns service.
 * @description Typed campaign CRUD service with validation, statistics, and UI utility helpers.
 * @author Sanjay Jangid (Jiggle frontend)
 * @email sanjay.jangid@dgtoohl.com
 * @date 2026-05-10
 * @version 0.0.0
 */
import { ApiService } from './apiService';
import { API_ENDPOINTS } from '../constants/api';
import type {
  Campaign,
  CampaignFormData,
  CampaignsApiResponse,
  CampaignsListResult,
  CampaignsPagination,
  CampaignStats,
} from '../types/domain/campaign';

export type {
  Campaign,
  CampaignFormData,
  CampaignsApiResponse,
  CampaignsListResult,
  CampaignsPagination,
  CampaignStats,
} from '../types/domain/campaign';

function normalizeCampaignsList(raw: unknown, fallbackPerPage: number): CampaignsListResult {
  const root = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};

  const pagSource =
    root['pagination'] ??
    (root['data'] && typeof root['data'] === 'object' && !Array.isArray(root['data'])
      ? (root['data'] as Record<string, unknown>)['pagination']
      : undefined);

  const pag =
    pagSource && typeof pagSource === 'object'
      ? (pagSource as Record<string, unknown>)
      : {};

  const page = Number(pag['page'] ?? 1) || 1;
  const per_page =
    Number(pag['per_page'] ?? fallbackPerPage) || fallbackPerPage;
  const total = Number(pag['total'] ?? 0) || 0;
  let total_page = Number(pag['total_page'] ?? pag['total_pages'] ?? 0) || 0;
  if (total_page === 0 && total > 0 && per_page > 0) {
    total_page = Math.ceil(total / per_page);
  }

  let rows: Campaign[] = [];
  const data = root['data'];
  if (Array.isArray(data)) {
    rows = data as Campaign[];
  } else if (data && typeof data === 'object') {
    const inner = data as Record<string, unknown>;
    if (Array.isArray(inner['data'])) rows = inner['data'] as Campaign[];
    else if (Array.isArray(inner['campaigns'])) rows = inner['campaigns'] as Campaign[];
  }

  return {
    campaigns: rows,
    pagination: { page, per_page, total, total_page: Math.max(1, total_page || 1) },
  };
}

/**
 * Campaigns Service
 * Handles all campaign-related API operations
 */
export class CampaignsService {
  /**
   * Fetch campaigns with server pagination (table listing).
   */
  static async listCampaigns(
    page: number = 1,
    perPage: number = 10,
  ): Promise<CampaignsListResult> {
    try {
      const query = new URLSearchParams({
        page: String(page),
        per_page: String(perPage),
        _: String(Date.now()),
      });
      const response = await ApiService.makeRequest<CampaignsApiResponse | Record<string, unknown>>(
        `${API_ENDPOINTS.CAMPAIGNS}?${query.toString()}`,
        'GET',
      );
      return normalizeCampaignsList(response, perPage);
    } catch (error) {
      throw error instanceof Error ? error : new Error('Failed to fetch campaigns');
    }
  }

  /**
   * Fetch all campaigns (dropdowns / stats).
   */
  static async getCampaigns(): Promise<Campaign[]> {
    const { campaigns } = await this.listCampaigns(1, 500);
    return campaigns;
  }

  /**
   * Fetch campaigns for a specific advertiser (GET /campaigns/advertiser/{id}).
   */
  static async getCampaignsByAdvertiser(
    advertiserId: string | number,
  ): Promise<Campaign[]> {
    const id = String(advertiserId).trim();
    if (!id) return [];

    try {
      const response = await ApiService.makeRequest<CampaignsApiResponse | Record<string, unknown>>(
        API_ENDPOINTS.campaignsByAdvertiser(id),
        'GET',
      );
      return normalizeCampaignsList(response, 500).campaigns;
    } catch (error) {
      throw error instanceof Error ? error : new Error('Failed to fetch campaigns for advertiser');
    }
  }

  /**
   * Create a new campaign
   */
  static async createCampaign(formData: CampaignFormData): Promise<{ message?: string }> {
      // Ensure all required fields are present and properly formatted (description is NOT required)
      if (!formData.name || !formData.advertiser_id || !formData.source) {
        throw new Error('Name, Advertiser, and Source are required');
      }
      
      // Create FormData as the API expects
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name.trim());
      formDataToSend.append('advertiser_id', formData.advertiser_id);
      formDataToSend.append('source', formData.source.trim());
      formDataToSend.append('description', formData.description.trim());
      
      
      const campaign = await ApiService.makeRequest<{ message?: string }>(
        API_ENDPOINTS.CAMPAIGNS,
        'POST',
        formDataToSend,
      );
      return campaign;
  }

  /**
   * Update an existing campaign
   */
  static async updateCampaign(id: number, formData: CampaignFormData): Promise<{ message?: string }> {
    try {
      // Create JSON data as the API expects for PUT requests
      const campaignData = {
        name: formData.name.trim(),
        advertiser_id: parseInt(formData.advertiser_id), // Convert to number for PUT
        source: parseInt(formData.source), // Convert to number for PUT
        description: formData.description.trim(),
      };
      
      
      const campaign = await ApiService.makeRequest<{ message?: string }>(
        `${API_ENDPOINTS.CAMPAIGNS}/${id}`,
        'PUT',
        campaignData,
      );
      return campaign;
    } catch (error) {
      
      // Handle API validation errors
      if (error instanceof Error) {
        if (error.message.includes('Validation failed')) {
          throw new Error('Please check the form for validation errors');
        }
        throw error;
      }
      throw new Error('Failed to update campaign');
    }
  }

  /**
   * Delete a campaign
   */
  static async deleteCampaign(id: number): Promise<{ message?: string }> {
    try {
      const campaign = await ApiService.makeRequest<{ message?: string }>(
        `${API_ENDPOINTS.CAMPAIGNS}/${id}`,
        'DELETE',
      );
      return campaign;
    } catch (error) {
      throw error instanceof Error ? error : new Error('Failed to delete campaign');
    }
  }

  /**
   * Toggle campaign status (active/paused)
   */
  static async toggleCampaignStatus(id: number, status: '0' | '1'): Promise<{ message?: string }> {
    try {
      const campaign = await ApiService.makeRequest<{ message?: string }>(
        `${API_ENDPOINTS.CAMPAIGNS}/status/${id}`,
        'POST',
        { status },
      );
      return campaign;
    } catch (error) {
      throw error instanceof Error ? error : new Error('Failed to toggle campaign status');
    }
  }

  /**
   * Get campaign statistics
   */
  static async getCampaignStats(): Promise<CampaignStats> {
    try {
      const campaigns = await this.getCampaigns();
      
      return {
        totalCampaigns: campaigns.length,
        activeCampaigns: campaigns.filter(c => c.status === '1').length,
        pausedCampaigns: campaigns.filter(c => c.status === '0').length,
        totalLeads: campaigns.reduce((sum, campaign) => sum + (campaign.total_leads || 0), 0),
      };
    } catch {
      return {
        totalCampaigns: 0,
        activeCampaigns: 0,
        pausedCampaigns: 0,
        totalLeads: 0,
      };
    }
  }

  /**
   * Validate campaign form data
   */
  static validateCampaignForm(formData: CampaignFormData, availableSources: { id: string; name: string }[] = []): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Campaign name validation
    if (!formData.name.trim()) {
      errors.push('Campaign name is required');
    } else if (formData.name.trim().length < 2) {
      errors.push('Campaign name must be at least 2 characters long');
    } else if (formData.name.trim().length > 100) {
      errors.push('Campaign name must be less than 100 characters');
    } else if (!/^[a-zA-Z0-9\s\-_&.()]+$/.test(formData.name.trim())) {
      errors.push('Campaign name contains invalid characters. Only letters, numbers, spaces, hyphens, underscores, ampersands, periods, and parentheses are allowed');
    }

    // Advertiser validation
    if (!formData.advertiser_id) {
      errors.push('Please select an advertiser');
    } else if (!/^\d+$/.test(formData.advertiser_id)) {
      errors.push('Invalid advertiser selection');
    }

    // Source validation
    if (!formData.source.trim()) {
      errors.push('Source is required');
    } else if (availableSources.length > 0 && !availableSources.some(source => source.id === formData.source)) {
      errors.push('Please select a valid source');
    }

    // Description validation (optional)
    if (formData.description.trim().length > 0 && formData.description.trim().length < 10) {
      errors.push('Description must be at least 10 characters long');
    } else if (formData.description.trim().length > 500) {
      errors.push('Description must be less than 500 characters');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Format date for display
   */
  static formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  /**
   * Get status color class
   */
  static getStatusColor(status: 'active' | 'paused'): string {
    return status === 'active' 
      ? 'bg-green-100 text-green-800 hover:bg-green-200' 
      : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200';
  }
}
